import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface SubmitMessage {
  type: "success" | "error";
  message: string;
}

export default function Hero() {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<SubmitMessage | null>(
    null
  );

  useEffect(() => {
    // Show text and buttons after 5.8 seconds (when logo animation completes)
    const timer = setTimeout(() => {
      setShowOverlay(true);
    }, 5800);

    return () => clearTimeout(timer);
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowContactModal(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showContactModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [showContactModal]);

  // Form handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || `HTTP error! status: ${response.status}`
        );
      }

      if (!result.success) {
        throw new Error(result.message || "Form submission failed");
      }

      // Success
      setSubmitMessage({
        type: "success",
        message: "Vielen Dank für deine Nachricht! Wir melden uns bald.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowContactModal(false);
        setSubmitMessage(null);
      }, 2000);
    } catch (error) {
      setSubmitMessage({
        type: "error",
        message: "Etwas ist schiefgelaufen. Bitte versuche es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative w-full overflow-hidden snap-start snap-always"
      style={{ height: "100dvh", backgroundColor: "#0f1114" }}
    >
      {/* Fullscreen Video Background */}
      <video
        autoPlay
        muted
        playsInline
        className="absolute inset-0 h-full w-full object-contain"
        onEnded={(e) => {
          // Keep video at last frame when it ends
          const video = e.currentTarget;
          video.pause();
        }}
      >
        <source src="/videos/landing-page.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay Content - appears after 7 seconds */}
      {showOverlay && (
        <div className="absolute inset-0 flex items-end justify-center px-6 pb-[12vh] md:pb-[22vh] pointer-events-none">
          <div className="max-w-4xl mx-auto text-center pointer-events-auto">
            {/* Sublines */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-3 mb-8"
            >
              <h1 className="text-2xl md:text-3xl text-white/90 font-light">
                Premium Vertical Content.
              </h1>
              <p className="text-lg md:text-xl text-white/80 font-light">
                Vertical Cinema for Brands & Creators.
                <br />
                Produziert wie Kino. Gemacht für Viralität.
              </p>
            </motion.div>

            {/* Location Tag */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                  stroke="rgba(173, 174, 167, 0.8)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                  stroke="rgba(173, 174, 167, 0.8)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-sm text-white/70 font-light">Berlin</span>
            </motion.div>
          </div>
        </div>
      )}

      {/* Scroll Indicator - Bottom center */}
      {showOverlay && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 pointer-events-auto"
        >
          <a href="#vertical-storytelling" className="inline-block group">
            <motion.svg
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ opacity: 0.5 }}
              className="group-hover:opacity-100 transition-opacity"
            >
              <path
                d="M7 10L12 15L17 10"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </a>
        </motion.div>
      )}

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowContactModal(false)}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="relative z-10 w-full max-w-lg rounded-2xl bg-[#0f1114] border border-white/10 shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">
                  Kontakt aufnehmen
                </h3>
                <button
                  className="text-white/70 hover:text-white transition-colors text-3xl leading-none"
                  onClick={() => setShowContactModal(false)}
                  aria-label="Close contact modal"
                >
                  ×
                </button>
              </div>
              <form className="px-6 py-5 space-y-4" onSubmit={handleSubmit}>
                {/* Name & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label
                      className="text-sm text-white/70"
                      htmlFor="modal-name"
                    >
                      Name *
                    </label>
                    <input
                      id="modal-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                      placeholder="Dein Name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      className="text-sm text-white/70"
                      htmlFor="modal-email"
                    >
                      E-Mail *
                    </label>
                    <input
                      id="modal-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label
                    className="text-sm text-white/70"
                    htmlFor="modal-phone"
                  >
                    Telefon (Optional)
                  </label>
                  <input
                    id="modal-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                    placeholder="+49 123 4567890"
                  />
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <label
                    className="text-sm text-white/70"
                    htmlFor="modal-subject"
                  >
                    Betreff *
                  </label>
                  <input
                    id="modal-subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                    placeholder="Worum geht es?"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label
                    className="text-sm text-white/70"
                    htmlFor="modal-message"
                  >
                    Nachricht *
                  </label>
                  <textarea
                    id="modal-message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all resize-none"
                    placeholder="Schreib uns, was du dir vorstellst..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${isSubmitting
                    ? "bg-white/50 text-black/50 cursor-not-allowed"
                    : "bg-white text-black hover:bg-white/90 hover:scale-[1.02]"
                    }`}
                >
                  {isSubmitting ? "Senden..." : "Absenden"}
                </button>

                {/* Success/Error Message */}
                {submitMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-4 p-3 rounded-lg text-sm ${submitMessage.type === "success"
                      ? "bg-green-500/20 text-green-300 border border-green-500/30"
                      : "bg-red-500/20 text-red-300 border border-red-500/30"
                      }`}
                  >
                    {submitMessage.message}
                  </motion.div>
                )}
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
